import type { MaintenanceStatus } from '@research-assistant/shared';
import { Clock, X, Wrench } from 'lucide-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button.js';

const DISMISS_KEY = 'maintenance-banner-dismissed';

function readDismissed(windowKey: string): boolean {
  try {
    return sessionStorage.getItem(`${DISMISS_KEY}:${windowKey}`) === 'true';
  } catch {
    return false;
  }
}

/**
 * Banner shown to ADMINS at the top of the app shell when
 * `MAINTENANCE_MODE=on`. Tells them that regular users see the downtime
 * screen, but they themselves are bypassing — so any changes they make
 * are still live. Dismissable for the current browser session
 * (sessionStorage, cleared on tab close).
 *
 * Dismissed flag is keyed by `${title}|${eta}` so it auto-resets when
 * ops starts a new maintenance window (different copy or new ETA) —
 * admin sees the banner again without having to re-enable manually.
 */
export function MaintenanceBanner({
  status,
}: {
  status: MaintenanceStatus;
}): JSX.Element | null {
  // Key the dismissed-flag by the maintenance-window identity (title +
  // eta). useMemo recomputes when the window flips. The initial render
  // reads sessionStorage lazily through useState — no setState-in-effect
  // cascade.
  const windowKey = useMemo(
    () => `${status.title}|${status.eta ?? ''}`,
    [status.title, status.eta],
  );
  const [dismissedKey, setDismissedKey] = useState<string | null>(() =>
    readDismissed(windowKey) ? windowKey : null,
  );

  function dismiss() {
    setDismissedKey(windowKey);
    try {
      sessionStorage.setItem(`${DISMISS_KEY}:${windowKey}`, 'true');
    } catch {
      // Best-effort; banner still hides for current render.
    }
  }

  // Recompute hidden each render from the (cheap) sessionStorage read so
  // a window flip surfaces the banner again without an effect.
  const hidden = dismissedKey === windowKey || readDismissed(windowKey);
  if (hidden) return null;

  return (
    <div
      className="flex shrink-0 items-center gap-3 border-b border-amber-300/40 bg-amber-100/60 px-4 py-2 text-amber-950 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100 sm:px-6"
      role="status"
      aria-live="polite"
    >
      <Wrench
        className="size-4 shrink-0 text-amber-700 dark:text-amber-300"
        aria-hidden
      />
      <p className="min-w-0 flex-1 truncate text-xs font-medium">
        <span className="font-semibold">Maintenance mode active.</span>
        <span className="ml-1 text-amber-900/80 dark:text-amber-100/80">
          Regular users see the downtime screen
          {status.eta ? <> · ETA {status.eta}</> : null}.
        </span>
      </p>
      {status.eta ? (
        <span className="hidden items-center gap-1 text-[11px] text-amber-900/80 dark:text-amber-100/80 sm:inline-flex">
          <Clock className="size-3" aria-hidden />
          {status.eta}
        </span>
      ) : null}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={dismiss}
        className="size-6 shrink-0 text-amber-900 hover:bg-amber-200/60 hover:text-amber-950 dark:text-amber-100 dark:hover:bg-amber-900/40"
        aria-label="Dismiss maintenance banner"
      >
        <X className="size-3.5" aria-hidden />
      </Button>
    </div>
  );
}
